// src/screens/OrderDetailsScreen.web.js - Web-compatible order details
import React, { useState, useEffect } from 'react';
import {
  View,
  ScrollView,
  StyleSheet,
  Text,
  Animated,
  Dimensions,
  Platform
} from 'react-native';
import {
  Card,
  Title,
  Paragraph,
  Button,
  Divider,
  Chip,
  ActivityIndicator,
  Surface,
  Avatar,
  TextInput
} from 'react-native-paper';
import { useFirebase } from '../context/FirebaseContext';
import StatusSelectorWeb from '../components/StatusSelector.web';

// Web-compatible icon component
const WebIcon = ({ name, size = 24, color = '#666' }) => {
  const iconMap = {
    'truck': '🚚',
    'calendar-clock': '📅⏰',
    'map-marker': '📍',
    'phone': '📞',
    'stairs': '🏢',
    'comment-text': '💬',
    'pizza': '🍕',
    'currency-ils': '₪',
    'content-copy': '📋',
    'whatsapp': '📱',
    'update': '🔄',
    'domain': '🏢',
  };
  
  return (
    <Text style={{ fontSize: size, color, textAlign: 'center' }}>
      {iconMap[name] || '◯'}
    </Text>
  );
};

// Generate UUID for web compatibility
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
};

const { width } = Dimensions.get('window');

const OrderDetailsScreenWeb = ({ route, navigation }) => {
  const { order: passedOrder, orderType, orderNumber: deepLinkedOrderNumber } = route.params || {};
  
  const [order, setOrder] = useState(passedOrder);
  const [loading, setLoading] = useState(!passedOrder);
  const [statuses, setStatuses] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState(passedOrder?.status);
  const [statusLoading, setStatusLoading] = useState(false);
  const [copyLoading, setCopyLoading] = useState(false);
  const [showStatusSelector, setShowStatusSelector] = useState(false);
  const [fadeAnim] = useState(new Animated.Value(0));
  
  const firebaseContext = useFirebase();
  const { getStatuses, updateOrderStatus, updateOrder, getOrderByNumber } = firebaseContext;

  const [phoneNumber, setPhoneNumber] = useState('');
  const [sending, setSending] = useState(false);

  useEffect(() => {
    initializeScreen();
  }, []);

  // Helper function to find order document ID by orderNumber
  const findOrderDocumentId = async (orderNumber) => {
    try {
      console.log('🔄 Finding document ID for order:', orderNumber);
      const orderData = await getOrderByNumber(orderNumber);
      
      if (orderData && orderData.id) {
        console.log('✅ Found document ID:', orderData.id);
        return orderData.id;
      } else {
        console.error('❌ Order not found or missing ID:', orderData);
        throw new Error('Order not found or missing document ID');
      }
    } catch (error) {
      console.error('❌ Error finding document:', error);
      throw error;
    }
  };

  // Initialize screen
  const initializeScreen = async () => {
    try {
      // If no order but has orderNumber (deep link)
      if (!passedOrder && deepLinkedOrderNumber) {
        console.log('🔗 Loading order from deep link:', deepLinkedOrderNumber);
        await loadOrderFromNumber(deepLinkedOrderNumber);
      }
      
      await loadStatuses();
      
      // Animation
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 600,
        useNativeDriver: true,
      }).start();
      
    } catch (error) {
      console.error('Error initializing screen:', error);
      if (Platform.OS === 'web') {
        alert('לא ניתן לטעון את פרטי ההזמנה');
      }
    } finally {
      setLoading(false);
    }
  };

 // Fixed loadOrderFromNumber function for OrderDetailsScreen.web.js

const loadOrderFromNumber = async (orderNumber) => {
  try {
    setLoading(true);
    console.log('📱 Fetching order:', orderNumber);
    
    const fetchedOrder = await getOrderByNumber(orderNumber);
    
    if (fetchedOrder) {
      setOrder(fetchedOrder);
      setSelectedStatus(fetchedOrder.status);
      console.log('✅ Order loaded successfully');
    } else {
      if (Platform.OS === 'web') {
        alert(`הזמנה מספר ${orderNumber} לא נמצאה במערכת`);
      }
      if (navigation && navigation.goBack) {
        navigation.goBack();
      }
    }
  } catch (error) {
    console.error('Error loading order:', error);
    if (Platform.OS === 'web') {
      alert('לא ניתן לטעון את פרטי ההזמנה');
    }
    if (navigation && navigation.goBack) {
      navigation.goBack();
    }
  } finally {
    // ✅ FIXED: Always set loading to false when done
    setLoading(false);
  }
};

  // Helper functions
  const getSideName = (sideId, itemSides) => {
    if (!itemSides || !Array.isArray(itemSides)) {
      return sideId;
    }
    
    const side = itemSides.find(s => s.id === sideId);
    return side ? side.name : sideId;
  };

  const translatePizzaSize = (sizeValue) => {
    const sizeTranslations = {
      'full': 'מגש מלא',
      'half': 'חצי מגש',
      'quarter': 'רבע מגש',
      'small': 'קטן',
      'medium': 'בינוני',
      'large': 'גדול',
      'xl': 'גדול במיוחד'
    };
    
    return sizeTranslations[sizeValue] || sizeValue;
  };

  // Generate secure signing token
  const generateSigningToken = () => {
    return generateUUID();
  };
  
  // Generate signature URL
  const generateSignatureUrl = (orderNumber, signingToken) => {
    const baseUrl = 'https://orderhr.co.il';
    return `${baseUrl}/customer-sign/${orderNumber}?token=${signingToken}`;
  };

  const shortenLink = async (longUrl) => {
    try {
      const response = await fetch(`https://tinyurl.com/api-create.php?url=${encodeURIComponent(longUrl)}`);
      const shortUrl = await response.text();
      return shortUrl;
    } catch (error) {
      console.error('Error shortening link:', error);
      return longUrl;
    }
  };

  const generateMapLinks = async (address) => {
    const encodedAddress = encodeURIComponent(address);
    const googleLong = `https://maps.google.com/?q=${encodedAddress}`;
    const wazeLong = `https://waze.com/ul?q=${encodedAddress}&navigate=yes`;

    const googleShort = await shortenLink(googleLong);
    const wazeShort = await shortenLink(wazeLong);

    return {
      google: googleShort,
      waze: wazeShort,
    };
  };

  // Validate phone number (Israeli format)
  const validatePhoneNumber = (phone) => {
    const cleanPhone = phone.replace(/\D/g, '');
    
    const patterns = [
      /^972[2-9]\d{7,8}$/,
      /^0[2-9]\d{7,8}$/,
      /^[2-9]\d{7,8}$/
    ];
    
    return patterns.some(pattern => pattern.test(cleanPhone));
  };

  // Format order details for copying
  const formatOrderDetailsForCopy = async (orderData, signatureUrl) => {
    const mapLinks = await generateMapLinks(order.fullAddress);
    
    return `📍 כתובת משלוח - הזמנה ${orderData.orderNumber}

🏢 ${orderData.organizationName}
📍 ${orderData.fullAddress}${orderData.floorNumber ? `\nקומה: ${orderData.floorNumber}` : ''}
📞 ${orderData.contactPhone || 'לא צוין'}

🗺️ קישורי ניווט:
Google Maps: ${mapLinks.google}
Waze: ${mapLinks.waze}

✍️ קישור לחתימה דיגיטלית:
${signatureUrl}`;
  };

  // Copy order details function
  const copyOrderDetails = async () => {
    try {
      setCopyLoading(true);
      
      let orderId = order.id;
      if (!orderId || orderId.trim() === '') {
        console.log('⚠️ Order ID is empty, finding document by orderNumber:', order.orderNumber);
        orderId = await findOrderDocumentId(order.orderNumber);
        setOrder(prev => ({ ...prev, id: orderId }));
      }
      
      const signingToken = generateSigningToken();
      const signatureUrl = generateSignatureUrl(order.orderNumber, signingToken);
      
      const updateData = {
        signingTokenDelivery: signingToken,
        signatureStatusDelivery: 'pending',
        signatureCreatedAtDelivery: new Date().toISOString(),
        signatureExpiryDelivery: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
        updatedAtDelivery: new Date().toISOString()
      };
      
      await updateOrder(orderId, updateData);
      
      const formattedDetails = await formatOrderDetailsForCopy(order, signatureUrl);
      
      // Copy to clipboard (web compatible)
      if (Platform.OS === 'web' && navigator.clipboard) {
        await navigator.clipboard.writeText(formattedDetails);
      } else {
        // Fallback for older browsers or React Native
        const textArea = document.createElement('textarea');
        textArea.value = formattedDetails;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand('copy');
        document.body.removeChild(textArea);
      }
      
      if (Platform.OS === 'web') {
        alert('פרטי הכתובת וקישור החתימה הועתקו ללוח.');
      }
      
    } catch (error) {
      console.error('Error copying order details:', error);
      if (Platform.OS === 'web') {
        alert('לא ניתן להעתיק את פרטי ההזמנה');
      }
    } finally {
      setCopyLoading(false);
    }
  };

// Copy and send to delivery person via WhatsApp - FIXED for Web
const copyAndSendToDelivery = async () => {
  try {
    if (!phoneNumber.trim()) {
      if (Platform.OS === 'web') {
        alert('אנא הזן מספר טלפון לשליח');
      }
      return;
    }

    if (!validatePhoneNumber(phoneNumber)) {
      if (Platform.OS === 'web') {
        alert('מספר הטלפון לא תקין. אנא הזן מספר טלפון ישראלי תקין');
      }
      return;
    }

    setSending(true);
    
    let orderId = order.id;
    if (!orderId || orderId.trim() === '') {
      console.log('⚠️ Order ID is empty, finding document by orderNumber:', order.orderNumber);
      orderId = await findOrderDocumentId(order.orderNumber);
      setOrder(prev => ({ ...prev, id: orderId }));
    }
    
    const signingToken = generateSigningToken();
    const signatureUrl = generateSignatureUrl(order.orderNumber, signingToken);
    
    const updateData = {
      signingTokenDelivery: signingToken,
      signatureStatusDelivery: 'pending',
      signatureCreatedAtDelivery: new Date().toISOString(),
      signatureExpiryDelivery: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      updatedAtDelivery: new Date().toISOString()
    };
    
    await updateOrder(orderId, updateData);
    
    const formattedDetails = await formatOrderDetailsForCopy(order, signatureUrl);
    
    // Copy to clipboard
    if (Platform.OS === 'web' && navigator.clipboard) {
      await navigator.clipboard.writeText(formattedDetails);
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = formattedDetails;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
    }
    
    // Format phone number for WhatsApp Web
    const formatPhoneForWhatsApp = (phone) => {
      // Remove all non-digit characters
      let cleanPhone = phone.replace(/\D/g, '');
      
      // Add country code if missing
      if (cleanPhone.startsWith('0')) {
        cleanPhone = '972' + cleanPhone.substring(1);
      } else if (!cleanPhone.startsWith('972')) {
        cleanPhone = '972' + cleanPhone;
      }
      
      return cleanPhone;
    };

    // Send via WhatsApp Web
    try {
      const formattedPhone = formatPhoneForWhatsApp(phoneNumber);
      const encodedMessage = encodeURIComponent(formattedDetails);
      const whatsappUrl = `https://web.whatsapp.com/send?phone=${formattedPhone}&text=${encodedMessage}`;
      
      // Open WhatsApp Web in a new tab
      window.open(whatsappUrl, '_blank');
      
      if (Platform.OS === 'web') {
        alert('הפרטים הועתקו ללוח ו-WhatsApp Web נפתח עם ההודעה המוכנה. לחץ "שלח" ב-WhatsApp לשליחת ההודעה.');
      }
      
      // Clear phone number after successful operation
      setPhoneNumber('');
      
    } catch (whatsappError) {
      console.error('Error opening WhatsApp Web:', whatsappError);
      if (Platform.OS === 'web') {
        alert('הפרטים הועתקו ללוח. לא ניתן היה לפתוח את WhatsApp Web אוטומטיה. שלח את הטקסט הבלוח לשליח ידנית.');
      }
    }
    
  } catch (error) {
    console.error('Error copying and sending order details:', error);
    if (Platform.OS === 'web') {
      alert('לא ניתן להעתיק ולשלוח את פרטי ההזמנה');
    }
  } finally {
    setSending(false);
  }
};

  const loadStatuses = async () => {
    try {
      const statusList = await getStatuses();
      setStatuses(statusList);
    } catch (error) {
      console.error('Error loading statuses:', error);
    }
  };

  // Handle status update
  const handleStatusUpdate = async (statusId) => {
    try {
      setStatusLoading(true);
      
      let orderId = order.id;
      
      if (!orderId || orderId.trim() === '') {
        console.log('⚠️ Order ID is empty, finding document by orderNumber:', order.orderNumber);
        
        try {
          orderId = await findOrderDocumentId(order.orderNumber);
          console.log('✅ Found document ID:', orderId);
          setOrder(prev => ({ ...prev, id: orderId }));
        } catch (fetchError) {
          console.error('Error finding document:', fetchError);
          if (Platform.OS === 'web') {
            alert('לא ניתן למצוא את ההזמנה במערכת');
          }
          return;
        }
      }
      
      if (!orderId) {
        if (Platform.OS === 'web') {
          alert('מזהה הזמנה לא תקין');
        }
        return;
      }
      
      console.log('🔄 Updating order with ID:', orderId);
      const result = await updateOrderStatus(orderId, statusId);
      
      if (result.success) {
        const newStatus = statuses.find(s => s.id === statusId);
        
        if (!newStatus) {
          throw new Error('Status not found');
        }

        setSelectedStatus(newStatus.statusName);
        setShowStatusSelector(false);

        if (Platform.OS === 'web') {
          alert('סטטוס ההזמנה עודכן בהצלחה');
        }
      } else {
        if (Platform.OS === 'web') {
          alert('לא ניתן לעדכן את סטטוס ההזמנה');
        }
      }
    } catch (error) {
      if (Platform.OS === 'web') {
        alert('אירעה שגיאה בעדכון הסטטוס');
      }
      console.error('Error updating status:', error);
    } finally {
      setStatusLoading(false);
    }
  };

  // Loading state
  if (loading || !order) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#2196F3" />
        <Text style={styles.loadingText}>טוען פרטי הזמנה...</Text>
      </View>
    );
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('he-IL', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'הצעת מחיר': return '#FF9800';
      case 'אושר': return '#4CAF50';
      case 'נדחה': return '#F44336';
      case 'בהכנה': return '#2196F3';
      case 'מוכן לאיסוף': return '#9C27B0';
      case 'סופק': return '#4CAF50';
      default: return '#607D8B';
    }
  };

  const InfoItem = ({ icon, title, description, color = '#666' }) => (
    <Surface style={styles.infoItem} elevation={1}>
      <View style={styles.infoIconContainer}>
        <Avatar.Icon 
          size={40} 
          icon={() => <WebIcon name={icon} size={20} color={color} />}
          style={[styles.infoIcon, { backgroundColor: color + '20' }]}
        />
      </View>
      <View style={styles.infoTextContainer}>
        <Text style={styles.infoTitle}>{title}</Text>
        <Text style={styles.infoDescription}>{description}</Text>
      </View>
    </Surface>
  );

  const renderItemDetails = (item, index) => {
    return (
      <Animated.View 
        key={index} 
        style={[
          styles.itemCard,
          {
            opacity: fadeAnim,
            transform: [{
              translateY: fadeAnim.interpolate({
                inputRange: [0, 1],
                outputRange: [50, 0]
              })
            }]
          }
        ]}
      >
        <Surface style={styles.itemSurface} elevation={3}>
          <View style={styles.itemHeader}>
            <View style={styles.itemIconContainer}>
              <Avatar.Icon 
                size={50} 
                icon={() => <WebIcon name="pizza" size={24} color="#fff" />}
                style={styles.itemIcon}
              />
            </View>
            <View style={styles.itemInfo}>
              <Title style={styles.itemName}>{item.itemName}</Title>
              <View style={styles.itemMeta}>
                <Chip mode="flat" style={styles.quantityChip} textStyle={styles.chipText}>
                  כמות: {item.quantity}
                </Chip>
                {item.price && (
                  <Chip mode="flat" style={styles.priceChip} textStyle={styles.priceChipText}>
                    ₪{item.price.Fprice || item.price}
                  </Chip>
                )}
              </View>
            </View>
          </View>

          {/* Pizza Sides Section */}
          {(item.pizzaSideSelections && Object.keys(item.pizzaSideSelections).length > 0) && (
            <View style={styles.sidesContainer}>
              <Text style={styles.sidesTitle}>🍕 תוספות פיצה</Text>
              <View style={styles.sidesGrid}>
                {Object.entries(item.pizzaSideSelections).map(([sideId, sizeValue]) => (
                  <Chip key={sideId} mode="outlined" style={styles.sideChip} textStyle={styles.sideChipText}>
                    {getSideName(sideId, item.sides)} - {translatePizzaSize(sizeValue)}
                  </Chip>
                ))}
              </View>
            </View>
          )}

          {/* Regular Sides Section */}
          {(item.applicableItems && item.applicableItems.length > 0) && (
            <View style={styles.sidesContainer}>
              <Text style={styles.sidesTitle}>➕ תוספות נוספות</Text>
              <View style={styles.sidesGrid}>
                {item.applicableItems.map((addon, idx) => (
                  <Chip key={idx} mode="outlined" style={styles.sideChip} textStyle={styles.sideChipText}>
                    {addon.name || addon}
                  </Chip>
                ))}
              </View>
            </View>
          )}
        </Surface>
      </Animated.View>
    );
  };

  return (
    <ScrollView style={styles.container} showsVerticalScrollIndicator={false}>
      {/* Back Button for Web */}
      {Platform.OS === 'web' && navigation && navigation.goBack && (
        <View style={styles.webBackButton}>
          <Button
            mode="outlined"
            onPress={navigation.goBack}
            icon={() => <WebIcon name="arrow-back" size={16} color="#2196F3" />}
          >
            חזור
          </Button>
        </View>
      )}

      {/* Hero Header */}
      <View style={styles.heroHeader}>
        <View style={styles.heroContent}>
          <View style={styles.orderNumberContainer}>
            <Text style={styles.orderLabel}>הזמנה מספר</Text>
            <Text style={styles.orderNumber}>{order.orderNumber}</Text>
          </View>
          <Chip 
            mode="flat" 
            style={[styles.statusChip, { backgroundColor: getStatusColor(selectedStatus) }]}
            textStyle={styles.statusText}
          >
            {selectedStatus}
          </Chip>
        </View>
        
        <View style={styles.organizationContainer}>
          <WebIcon name="domain" size={24} color="#fff" />
          <Text style={styles.organizationName}>{order.organizationName}</Text>
        </View>
      </View>

      {/* Order Information */}
      <Animated.View style={[styles.section, { opacity: fadeAnim }]}>
        <Text style={styles.sectionTitle}>📋 פרטי ההזמנה</Text>
        
        <InfoItem
          icon="truck"
          title="סוג משלוח"
          description={order.pickUpType}
          color="#FF9800"
        />
        
        <InfoItem
          icon="calendar-clock"
          title="תאריך רצוי לאספקה"
          description={formatDate(order.customerOrderDate)}
          color="#4CAF50"
        />
        
        <InfoItem
          icon="map-marker"
          title="כתובת המשלוח"
          description={order.fullAddress}
          color="#2196F3"
        />

        {order.contactPhone && (
          <InfoItem
            icon="phone"
            title="טלפון ליצירת קשר"
            description={order.contactPhone}
            color="#9C27B0"
          />
        )}
        
        {order.floorNumber && (
          <InfoItem
            icon="stairs"
            title="קומה"
            description={`קומה ${order.floorNumber}`}
            color="#9C27B0"
          />
        )}
        
        {order.comments && (
          <InfoItem
            icon="comment-text"
            title="הערות מיוחדות"
            description={order.comments}
            color="#607D8B"
          />
        )}

        <InfoItem
          icon="truck"
          title="מחיר משלוח"
          description={order.franchiseeDeliveryComission?.toFixed(1)}
          color="#FF9800"
        />
        
      </Animated.View>

      {/* Order Items */}
      {order.items && order.items.length > 0 && (
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>🍽️ פריטי ההזמנה</Text>
          {order.items.map((item, index) => renderItemDetails(item, index))}
         
        </View>
      )}

      {/* Total */}
      <Animated.View style={[styles.section, { opacity: fadeAnim }]}>
        <Surface style={styles.totalCard} elevation={4}>
          <View style={styles.totalGradient}>
            <View style={styles.totalContent}>
              <View style={styles.totalIconContainer}>
                <WebIcon name="currency-ils" size={32} color="#fff" />
              </View>
              <View style={styles.totalTextContainer}>
                <Text style={styles.totalLabel}>סכום כולל</Text>
                <Text style={styles.totalAmount}>₪{order.totalAmount - order.totalProfit}</Text>
              </View>
            </View>
          </View>
        </Surface>
      </Animated.View>

      {/* Action Buttons */}
      <Animated.View style={[styles.section, { opacity: fadeAnim }]}>
        {/* Copy Details Button */}
        <Surface style={styles.actionCard} elevation={3}>
          <Button
            mode="contained"
            onPress={copyOrderDetails}
            style={[styles.statusButton, { backgroundColor: '#FF9800', marginBottom: 12 }]}
            contentStyle={styles.statusButtonContent}
            labelStyle={styles.statusButtonLabel}
            disabled={copyLoading}
            icon={() => <WebIcon name="content-copy" size={16} color="#fff" />}
          >
            {copyLoading ? <ActivityIndicator size="small" color="#fff" /> : '📋 העתק פרטים'}
          </Button>

          <TextInput
            placeholder="הזן מספר טלפון לשליח (למשל: 0501234567)"
            keyboardType="phone-pad"
            value={phoneNumber}
            onChangeText={setPhoneNumber}
            style={styles.phoneInput}
            mode="outlined"
            right={<TextInput.Icon icon={() => <WebIcon name="phone" size={16} />} />}
          />

          <Button
            mode="contained"
            onPress={copyAndSendToDelivery}
            style={[styles.statusButton, { backgroundColor: '#4CAF50', marginTop: 12 }]}
            contentStyle={styles.statusButtonContent}
            labelStyle={styles.statusButtonLabel}
            disabled={sending || !phoneNumber.trim()}
            icon={() => <WebIcon name="whatsapp" size={16} color="#fff" />}
          >
            {sending ? <ActivityIndicator size="small" color="#fff" /> : '📋 העתק ושלח לשליח'}
          </Button>
        </Surface>

        {/* Update Status Button */}
        <Surface style={[styles.actionCard, { marginTop: 16 }]} elevation={3}>
          <Button
            mode="contained"
            onPress={() => setShowStatusSelector(true)}
            style={styles.statusButton}
            contentStyle={styles.statusButtonContent}
            labelStyle={styles.statusButtonLabel}
            disabled={statusLoading}
            icon={() => <WebIcon name="update" size={16} color="#fff" />}
          >
            {statusLoading ? (
              <ActivityIndicator size="small" color="#fff" />
            ) : (
              'עדכן סטטוס הזמנה'
            )}
          </Button>
        </Surface>
      </Animated.View>

      <StatusSelectorWeb
        visible={showStatusSelector}
        onDismiss={() => setShowStatusSelector(false)}
        statuses={statuses}
        currentStatus={selectedStatus}
        onStatusSelect={handleStatusUpdate}
        requiresSignature={order.requiresSignature} 
      />
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: '#f8f9fa',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#666',
    textAlign: 'center',
  },
  webBackButton: {
    padding: 16,
    backgroundColor: '#fff',
    borderBottomWidth: 1,
    borderBottomColor: '#e0e0e0',
  },
  heroHeader: {
    paddingTop: 40,
    paddingBottom: 30,
    paddingHorizontal: 20,
    backgroundColor: '#2196F3',
    borderBottomLeftRadius: 30,
    borderBottomRightRadius: 30,
  },
  heroContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 20,
  },
  orderNumberContainer: {
    flex: 1,
  },
  orderLabel: {
    fontSize: 14,
    color: '#e3f2fd',
    marginBottom: 4,
  },
  orderNumber: {
    fontSize: 28,
    fontWeight: 'bold',
    color: '#fff',
  },
  statusChip: {
    borderRadius: 20,
    paddingHorizontal: 12,
  },
  statusText: {
    fontWeight: 'bold',
    color: '#fff',
    fontSize: 12,
  },
  organizationContainer: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  organizationName: {
    fontSize: 18,
    fontWeight: '600',
    color: '#fff',
    marginLeft: 8,
  },
  section: {
    paddingHorizontal: 20,
    paddingVertical: 16,
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 16,
  },
  infoItem: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
    marginBottom: 12,
    borderRadius: 16,
    backgroundColor: '#fff',
  },
  infoIconContainer: {
    marginRight: 16,
  },
  infoIcon: {
    backgroundColor: '#e3f2fd',
  },
  infoTextContainer: {
    flex: 1,
  },
  infoTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#666',
    marginBottom: 4,
  },
  infoDescription: {
    fontSize: 16,
    color: '#333',
    fontWeight: '500',
  },
  itemCard: {
    marginBottom: 16,
  },
  itemSurface: {
    borderRadius: 20,
    backgroundColor: '#fff',
    overflow: 'hidden',
  },
  itemHeader: {
    flexDirection: 'row',
    padding: 20,
    alignItems: 'center',
  },
  itemIconContainer: {
    marginRight: 16,
  },
  itemIcon: {
    backgroundColor: '#FF9800',
  },
  itemInfo: {
    flex: 1,
  },
  itemName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 8,
  },
  itemMeta: {
    flexDirection: 'row',
    gap: 8,
  },
  quantityChip: {
    backgroundColor: '#e8f5e8',
  },
  priceChip: {
    backgroundColor: '#fff3e0',
  },
  chipText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#4CAF50',
  },
  priceChipText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#FF9800',
  },
  sidesContainer: {
    paddingHorizontal: 20,
    paddingBottom: 20,
  },
  sidesTitle: {
    fontSize: 14,
    fontWeight: 'bold',
    color: '#333',
    marginBottom: 12,
  },
  sidesGrid: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  sideChip: {
    marginBottom: 4,
  },
  sideChipText: {
    fontSize: 11,
    color: '#666',
  },
  totalCard: {
    borderRadius: 20,
    overflow: 'hidden',
  },
  totalGradient: {
    backgroundColor: '#4CAF50',
    borderRadius: 20,
  },
  totalContent: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 24,
  },
  totalIconContainer: {
    backgroundColor: 'rgba(255,255,255,0.2)',
    borderRadius: 20,
    padding: 12,
    marginRight: 16,
  },
  totalTextContainer: {
    flex: 1,
  },
  totalLabel: {
    fontSize: 14,
    color: '#e8f5e8',
    marginBottom: 4,
  },
  totalAmount: {
    fontSize: 32,
    fontWeight: 'bold',
    color: '#fff',
  },
  actionCard: {
    borderRadius: 16,
    backgroundColor: '#fff',
    padding: 16,
  },
  statusButton: {
    borderRadius: 12,
    backgroundColor: '#2196F3',
  },
  statusButtonContent: {
    height: 56,
  },
  statusButtonLabel: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#fff',
  },
  phoneInput: {
    backgroundColor: '#fff',
    marginHorizontal: 0,
  },
});

export default OrderDetailsScreenWeb;